import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeKatex from 'rehype-katex'
import rehypeHighlight from 'rehype-highlight'
import 'katex/dist/katex.min.css'
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { UploadedFileInfo } from 'types/upload';
import feedbackAPI from 'apis/feedbackAPI';
import FeedbackModal from './FeedbackModal';
import { parseCanvasLinks } from 'utils/canvasUtils';
import { cn } from 'libs/classNameUtils';

// Add custom styles for KaTeX to handle overflow
const style = document.createElement('style');
style.textContent = `
  .katex-display {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5rem 0;
    margin: 0.5rem 0;
  }
  .katex {
    font-size: 0.95em;
    word-break: break-word;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('katex-overflow-fix')) {
  style.id = 'katex-overflow-fix';
  document.head.appendChild(style);
}

type BubbleProps = {
  message: string;
  index: number;
  isUserMessage: boolean;
  isLoading?: boolean;
  fontColor?: string;
  fontSize?: string;
  width?: string;
  radius?: string;
  position?: string;
  padding?: string;
  textAlign?: string;
  textWrap?: string;
  isMarkdown?: boolean;
  uploadFiles?: UploadedFileInfo[];
  chunkId?: number;
  sessionId?: string;
  feedback?: {
    rating: 'positive' | 'negative' | null;
    comment?: string;
  } | null;
  isDisplayFeedback?: boolean;
  onFeedbackUpdate?: (feedback: { rating: 'positive' | 'negative' | null; comment?: string } | null) => void;
};

const splitMessageIntoParagraphs = (message: string) => {
  return message.split("\n").map((paragraph, index) => (
    <p key={index}>
      {paragraph}
      <br />
    </p>
  ));
};

const Bubble = ({
  message,
  index,
  isUserMessage,
  isLoading = false,
  fontColor = "BABlack",
  fontSize = "text-base",
  width = "max-w-[90%]",
  radius = "rounded-[10px]",
  position = "relative",
  padding = "p-[10px]",
  textAlign = "left",
  textWrap = "wrap",
  isMarkdown = false,
  uploadFiles,
  chunkId,
  sessionId,
  feedback,
  isDisplayFeedback = true,
  onFeedbackUpdate,
}: BubbleProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFeedbackHovered, setIsFeedbackHovered] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(feedback);
  const [feedbackModal, setFeedbackModal] = useState<{
    isVisible: boolean;
    type: 'positive' | 'negative';
    position: { x: number; y: number };
    initialComment: string;
  }>({
    isVisible: false,
    type: 'positive',
    position: { x: 0, y: 0 },
    initialComment: '',
  });
  const bubbleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentFeedback(feedback);
  }, [feedback]);

  const handleFeedbackClick = (type: 'positive' | 'negative', event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    
    const existingComment = currentFeedback && currentFeedback.rating === type ? currentFeedback.comment || '' : '';
    
    setFeedbackModal({
      isVisible: true,
      type,
      position: {
        x: rect.right,
        y: rect.bottom,
      },
      initialComment: existingComment,
    });
  };

  const handleFeedbackSubmit = async (feedback: string | null, type: 'positive' | 'negative' | null) => {
    const chatMode = process.env.REACT_APP_CHAT_MODE || 'bot';
    try {
      if (!sessionId || chunkId === undefined) {
        console.error('Missing required parameters: sessionId or chunkId');
        return;
      }

      const result = await feedbackAPI.submitFeedback(sessionId, {
        chunkId,
        responseIdx: index,
        rating: type,
        comment: feedback || undefined,
      }, chatMode);

      if (result.error) {
        console.error('Failed to submit feedback:', result.error);
      } else {
        const newFeedback = feedback === null ? null : {
          rating: type,
          comment: feedback || undefined,
        };
        
        setCurrentFeedback(newFeedback);
        
        if (onFeedbackUpdate) {
          onFeedbackUpdate(newFeedback);
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const closeFeedbackModal = () => {
    setFeedbackModal(prev => ({ ...prev, isVisible: false }));
  };

  const handleContainerMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      if (!isFeedbackHovered && !feedbackModal.isVisible) {
        setIsHovered(false);
      }
    }, 100);
  };
  
  const key = isUserMessage ? `user-${index}` : `agent-${index}`;

  // Colors for user vs bot
  const bubbleBackground = isUserMessage ? 'bg-BABLPink' : 'bg-BABLBlue';
  const headerColor = "isUserMessage ? 'text-BABMPink' : 'text-BABMBlue';"
  const textColor = 'text-BABlack';
  const tableColBg = isUserMessage ? 'bg-BABMPink' : 'bg-BABMBlue';
  const tableBorder = isUserMessage ? 'border-BADPink' : 'border-BADBlue';

  // Bubble tail styling
  const bubbleTailClasses = [
    `before:content-['']`,
    'before:w-0',
    'before:h-0',
    'before:absolute',
    'before:top-0',
    'before:border-t-[15px]',
    'before:border-b-[15px]',
  ];

  const bubbleTailPositionClasses = isUserMessage
    ? ['before:border-l-[15px]', 'before:border-l-BALPink', 'before:border-r-0', 'before:border-t-transparent', 'before:border-b-transparent', 'before:-right-[15px]']
    : ['before:border-r-[15px]', 'before:border-r-BALBlue', 'before:border-l-0', 'before:border-t-transparent', 'before:border-b-transparent', 'before:-left-[15px]'];

  // Main bubble classes - scales down and centers on mobile
  const bubbleClasses = cn(
    ...bubbleTailClasses,
    ...bubbleTailPositionClasses,
    bubbleBackground,
    textColor,
    'relative',
    'rounded-[10px]',
    'p-1.5 sm:p-2 md:p-3',
    'w-full max-w-xs xs:max-w-sm sm:max-w-sm md:max-w-md lg:max-w-xl xl:max-w-2xl',
    'text-xs xs:text-sm sm:text-base',
    'break-words',
    'word-break',
    'overflow-hidden',
    'min-w-0',
    isLoading && `after:content-[''] after:animate-loadingDots`,
  );

  // Center alignment on mobile (under md breakpoint), normal alignment on desktop
  const containerJustify = isUserMessage 
    ? "flex justify-center md:justify-end" 
    : "flex justify-center md:justify-start";
  const marginClass = isUserMessage 
    ? "m-0.5 xs:m-1 sm:m-1.5 md:m-1.5 md:mx-3" 
    : "m-0.5 xs:m-1 sm:m-1.5 md:m-1.5 md:ml-3";

  return (
    <div 
      className={`${containerJustify} ${marginClass} group w-full px-2 xs:px-3 sm:px-4 md:px-0`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleContainerMouseLeave}
    >
      <div
        ref={bubbleRef}
        className={bubbleClasses}
        key={key}
      >
        <div className="mb-1">
          <strong className={headerColor}>{isUserMessage ? "You:" : "Bot:"}</strong>
        </div>
        {isMarkdown ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeRaw, rehypeHighlight]}
            components={{
              h1: ({ ...props }) => <h1 className={cn(`text-2xl sm:text-3xl font-bold mb-2 mt-4 text-wrap break-words`, textColor)}>{props.children}</h1>,
              h2: ({ ...props }) => <h2 className={cn(`text-xl sm:text-2xl font-semibold mb-2 mt-4 text-wrap break-words`, textColor)}>{props.children}</h2>,
              h3: ({ ...props }) => <h3 className={cn(`text-lg sm:text-xl font-semibold mb-1 mt-3 text-wrap break-words`, textColor)}>{props.children}</h3>,
              h4: ({ ...props }) => <h4 className={cn(`text-base sm:text-lg font-medium mt-3 text-wrap break-words`, textColor)}>{props.children}</h4>,
              h5: ({ ...props }) => <h5 className={cn(`text-sm sm:text-base font-medium mt-3 text-wrap break-words`, textColor)}>{props.children}</h5>,
              h6: ({ ...props }) => <h6 className={cn(`text-xs sm:text-sm font-medium mt-3 text-wrap break-words`, textColor)}>{props.children}</h6>,
              p: ({ ...props }) => {
                const processChildren = (children: React.ReactNode): React.ReactNode => {
                  return React.Children.map(children, (child) => {
                    if (typeof child === 'string') {
                      return parseCanvasLinks(child);
                    }
                    return child;
                  });
                };
                return <p className={cn(`my-2 break-words leading-relaxed text-wrap`, textColor)}>{processChildren(props.children)}</p>;
              },
              ul: ({ ...props }) => <ul className={cn(`mb-2 list-disc list-outside ml-6 text-wrap`, textColor)}>{props.children}</ul>,
              ol: ({ ...props }) => <ol className={cn(`mb-2 list-decimal list-outside ml-6 text-wrap`, textColor)}>{props.children}</ol>,
              li: ({ ...props }) => <li className={cn(`mb-1`, textColor)}>{props.children}</li>,
              blockquote: ({ ...props }) => (
                <blockquote className={cn(`border-l-4 border-gray-400 pl-4 italic my-2 text-wrap`, textColor)}>
                  {props.children}
                </blockquote>
              ),
              code: ({ className, children, ...props }: any) => {
                const isInline = !className;
                return isInline ? (
                  <code className={cn(`bg-gray-100 px-1 py-0.5 rounded text-xs sm:text-sm font-mono break-words overflow-x-auto`, isUserMessage ? 'text-BADPink' : 'text-BABlack')}>{children}</code>
                ) : (
                  <pre className='bg-gray-900 text-white p-2 sm:p-4 rounded overflow-x-auto text-xs sm:text-sm break-words whitespace-pre-wrap'>
                    <code className={className}>{children}</code>
                  </pre>
                );
              },
              table: ({ ...props }) => (
                <table className={cn(`border-collapse w-full my-2 sm:my-4 text-xs sm:text-sm overflow-x-auto block`, textColor)}>{props.children}</table>
              ),
              th: ({ ...props }: any) => {
                return <th className={cn('border p-0.5 sm:p-1 text-center text-wrap break-words text-xs sm:text-sm', tableBorder, tableColBg, textColor)} style={{}}>{props.children}</th>;
              },
              td: ({ ...props }: any) => {
                const align = props.style?.textAlign || 'left';
                const alignClass = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
                return <td className={cn('border p-0.5 sm:p-1 break-words text-wrap text-xs sm:text-sm', alignClass, tableBorder, textColor)} style={{}}>{props.children}</td>;
              },
              tr: ({ ...props }) => (
                <tr className={cn(`transition-colors duration-300 hover:bg-BALGray text-wrap`, textColor)}>{props.children}</tr>
              ),
            }}
          >
            {message}
          </ReactMarkdown>
        ) : (
          <div className={textColor}>
            {splitMessageIntoParagraphs(message)}
          </div>
        )}
        {uploadFiles && uploadFiles.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {uploadFiles.map(file => (
              <div key={file.documentId} className="text-xs bg-BALGray text-BADBlue break-all px-2 py-1 rounded-full">
                <span className="truncate max-w-[100px] sm:max-w-[120px] md:max-w-[150px] lg:max-w-[200px] inline-block" title={file.fileName}>
                  {file.fileName}
                </span>
              </div>
            ))}
          </div>
        )}

        {!isUserMessage && !isLoading && (
          <div 
            className="absolute bottom-2 right-2 flex gap-1 z-10"
            onMouseEnter={() => setIsFeedbackHovered(true)}
            onMouseLeave={() => setIsFeedbackHovered(false)}
          >
            {currentFeedback ? (
              <>
                <button
                  onClick={(e) => handleFeedbackClick('positive', e)}
                  className={`p-1.5 rounded-full border transition-colors shadow-sm ${
                    currentFeedback.rating === 'positive'
                      ? 'bg-green-100 border-green-400 text-green-600'
                      : 'bg-BAWhite border-BALGray text-BAMGray hover:border-green-400 hover:bg-green-50'
                  }`}
                  title={currentFeedback.rating === 'positive' && currentFeedback.comment ? `You liked this: "${currentFeedback.comment}"` : "Good response"}
                >
                  <ThumbsUp size={16} className={currentFeedback.rating === 'positive' ? 'text-green-600' : 'text-BAMGray hover:text-green-600'} />
                </button>
                <button
                  onClick={(e) => handleFeedbackClick('negative', e)}
                  className={`p-1.5 rounded-full border transition-colors shadow-sm ${
                    currentFeedback.rating === 'negative'
                      ? 'bg-red-100 border-red-400 text-red-600'
                      : 'bg-BAWhite border-BALGray text-BAMGray hover:border-red-400 hover:bg-red-50'
                  }`}
                  title={currentFeedback.rating === 'negative' && currentFeedback.comment ? `You didn't like this: "${currentFeedback.comment}"` : "Could be better"}
                >
                  <ThumbsDown size={16} className={currentFeedback.rating === 'negative' ? 'text-red-600' : 'text-BAMGray hover:text-red-600'} />
                </button>
              </>
            ) : (

              (isDisplayFeedback && (isHovered || isFeedbackHovered || feedbackModal.isVisible)) && (
                <>
                  <button
                    onClick={(e) => handleFeedbackClick('positive', e)}
                    className="p-1.5 rounded-full bg-BAWhite border border-BALGray hover:border-green-400 hover:bg-green-50 transition-colors shadow-sm"
                    title="Good response"
                  >
                    <ThumbsUp size={16} className="text-BAMGray hover:text-green-600" />
                  </button>
                  <button
                    onClick={(e) => handleFeedbackClick('negative', e)}
                    className="p-1.5 rounded-full bg-BAWhite border border-BALGray hover:border-red-400 hover:bg-red-50 transition-colors shadow-sm"
                    title="Could be better"
                  >
                    <ThumbsDown size={16} className="text-BAMGray hover:text-red-600" />
                  </button>
                </>
              )
            )}
          </div>
        )}
      </div>

      <FeedbackModal
        isVisible={feedbackModal.isVisible}
        feedbackType={feedbackModal.type}
        position={feedbackModal.position}
        onClose={closeFeedbackModal}
        onSubmit={handleFeedbackSubmit}
        initialComment={feedbackModal.initialComment}
      />
    </div>
  );
};

export default Bubble;
